/**
 * Atlas — Settings page.
 *
 * Surfaces account-management actions for every authenticated user:
 *   - Profile summary (read-only)
 *   - Privacy: data export (existing GDPR Art 20 endpoint)
 *   - Danger zone: delete account (GDPR Art 17 endpoint, password-gated)
 *
 * Pros also have a separate /pro/profile page for marketplace listing
 * fields — that's intentionally distinct from this page which is purely
 * about account-level actions.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import DeleteAccountModal from '../components/auth/DeleteAccountModal';

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  async function exportData() {
    setExporting(true);
    setExportError(null);
    try {
      // Backend returns a JSON blob — stream it into a download link so the
      // user gets a file rather than a printed-out browser tab.
      const res = await authAPI.exportMyData();
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `propatlas-data-${user?.email || 'export'}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err?.response?.data?.detail || err?.message || 'Export failed.');
    } finally {
      setExporting(false);
    }
  }

  // Called by the modal once the backend has confirmed deletion. We log out
  // (the access token has already been blacklisted server-side so this is
  // mostly a client-side cleanup) and bounce back to the public landing.
  async function onDeleted() {
    setDeleteOpen(false);
    try { await logout(); } catch {}
    navigate('/login', { replace: true, state: { flash: 'Your account has been deleted.' } });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 720 }}>
      <div>
        <div className="eyebrow">Account</div>
        <h1 className="serif" style={{ margin: '4px 0 0', fontSize: 28, color: 'var(--pa-navy)', fontWeight: 600 }}>
          Settings
        </h1>
      </div>

      {/* Profile summary */}
      <div className="card" style={{ padding: 20 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <div style={{
            width: 56, height: 56, borderRadius: 28, background: 'var(--pa-tint)',
            display: 'grid', placeItems: 'center', color: 'var(--pa-navy)', fontWeight: 700,
          }}>
            {((user?.first_name?.[0] || '') + (user?.last_name?.[0] || '')).toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="serif" style={{ fontWeight: 600, color: 'var(--pa-navy)' }}>
              {user?.first_name} {user?.last_name}
            </div>
            <div className="mono" style={{ fontSize: 12, color: 'var(--pa-slate-2)' }}>{user?.email}</div>
            <span style={{
              display: 'inline-block', marginTop: 6, padding: '2px 8px',
              background: 'var(--pa-tint)', color: 'var(--pa-navy)',
              fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase',
              borderRadius: 'var(--pa-radius-pill)', fontWeight: 700,
            }}>
              {user?.role}
            </span>
          </div>
        </div>
      </div>

      {/* Privacy */}
      <div className="card" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="eyebrow">Privacy</div>
        <h3 className="serif" style={{ margin: 0, color: 'var(--pa-navy)' }}>Your data</h3>
        <p style={{ margin: 0, fontSize: 14, color: 'var(--pa-slate-2)' }}>
          Download a copy of all personal data we hold about you — properties,
          timeline entries, EOIs, bookings, messages. Delivered as a JSON file.
        </p>
        {exportError ? (
          <div style={{
            padding: 10, background: 'var(--pa-negative-bg)', color: 'var(--pa-negative)',
            borderRadius: 'var(--pa-radius-md)', fontSize: 13,
          }}>{exportError}</div>
        ) : null}
        <div>
          <button className="btn outline" onClick={exportData} disabled={exporting}>
            {exporting ? 'Preparing…' : 'Download my data'}
          </button>
        </div>
      </div>

      {/* Danger zone */}
      <div className="card" style={{
        padding: 20, display: 'flex', flexDirection: 'column', gap: 12,
        border: '1px solid var(--pa-negative)',
      }}>
        <div className="eyebrow" style={{ color: 'var(--pa-negative)' }}>Danger zone</div>
        <h3 className="serif" style={{ margin: 0, color: 'var(--pa-navy)' }}>Delete account</h3>
        <p style={{ margin: 0, fontSize: 14, color: 'var(--pa-slate-2)' }}>
          Permanently erase your account and personal data. Your properties,
          EOIs, messages and uploads will be removed. Audit logs are retained
          with your identity scrubbed for legal compliance. This cannot be undone.
        </p>
        <div>
          <button className="btn danger" onClick={() => setDeleteOpen(true)}>
            Delete my account…
          </button>
        </div>
      </div>

      <DeleteAccountModal
        open={deleteOpen}
        user={user}
        onClose={() => setDeleteOpen(false)}
        onDeleted={onDeleted}
      />
    </div>
  );
}
